import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { prodigyGroupsAPI } from '../services/api';
import Navbar from '../components/common/Navbar';

const PRODIGY_MANAGER_URL = import.meta.env.VITE_PRODIGY_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:8085' : '/prodigy');
const PRODIGY_SESSION_URL = import.meta.env.VITE_PRODIGY_SESSION_URL || PRODIGY_MANAGER_URL;

// Fallback estático por si la API no responde
const FALLBACK_SERVICES = {
  pos: { name: 'POS Tagging', description: 'Etiquetado morfosintáctico', port: '8080', step: 1, color: '#2563eb' },
  features: { name: 'Features', description: 'Características morfológicas', port: '8081', step: 2, color: '#10b981' },
  relations: { name: 'Relations', description: 'Relaciones sintácticas', port: '8082', step: 3, color: '#f59e0b' },
  ner: { name: 'NER Tagging', description: 'Reconocimiento de entidades nombradas', port: '8083', step: 4, color: '#dc2626' },
};

const TOTAL_BATCHES = 60;
const BATCH_SIZE = 50;

const ProdigyPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(1);
  const [selectedService, setSelectedService] = useState('pos');
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [runningServices, setRunningServices] = useState({});
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [totalBatches, setTotalBatches] = useState(TOTAL_BATCHES);

  // Progreso personal del usuario (para detectar cuota completada)
  const [myProgress, setMyProgress] = useState({});
  // NC-68: batches asignados al usuario (multi-instancia por grupo)
  const [myBatches, setMyBatches] = useState({ groups: [] });
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [extraProcesses, setExtraProcesses] = useState([]);
  const [selectedExtraType, setSelectedExtraType] = useState('');
  const [startingExtra, setStartingExtra] = useState(false);
  const [extraError, setExtraError] = useState(null);
  
  // Puertos activos (solo admin)
  const [allPorts, setAllPorts] = useState([]);
  const [exportingBackup, setExportingBackup] = useState(false);
  const [backupError, setBackupError] = useState(null);

  // Estado para configuración de buffer
  const [bufferConfig, setBufferConfig] = useState({
    instant_submit: false,
    batch_size: 3,
  });
  const [showBufferConfig, setShowBufferConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [configSuccess, setConfigSuccess] = useState(false);

  // Verificar si el usuario es admin (is_superuser del backend o role === 'admin')
  const isAdmin = user?.is_superuser === true || user?.role === 'admin';

  // Miembro de un grupo (my-progress devuelve claves "{tipo}-g{N}") pero /my-batches viene vacío
  const hasGroupWithoutBatches =
    (myBatches.groups?.length || 0) === 0 &&
    Object.keys(myProgress).some((k) => /-g\d+$/.test(k));

  // Cargar tipos de anotación dinámicamente desde la API
  const fetchAnnotationTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.types && data.types.length > 0) {
          const dynamicServices = {};
          data.types.forEach(t => {
            dynamicServices[t.id] = {
              name: t.name,
              description: t.description || '',
              port: String(t.port),
              step: t.step,
              color: t.color || '#2563eb',
            };
          });
          setServices(dynamicServices);
        }
        if (data.total_batches) setTotalBatches(data.total_batches);
      }
    } catch (error) {
      console.log('Usando configuración de servicios por defecto');
    }
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        fetchAnnotationTypes(),
        fetchRunningServices(),
        fetchBufferConfig(),
        fetchMyProgress(),
        fetchExtraProcesses(),
        fetchMyBatches(),
        ...(isAdmin ? [fetchAllPorts()] : []),
      ]);
      setLoading(false);
    };
    init();
    let myBatchesTick = 0;
    const interval = setInterval(() => {
      fetchRunningServices();
      fetchMyProgress();
      fetchExtraProcesses();
      // /api/prodigy/my-batches es relativamente pesado cuando hay muchas instancias.
      // Lo refrescamos cada 30s en lugar de cada 10s para reducir carga.
      myBatchesTick += 1;
      if (myBatchesTick % 3 === 0) {
        fetchMyBatches();
      }
      if (isAdmin) fetchAllPorts();
    }, 10000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const fetchRunningServices = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRunningServices(data.services || {});
      } else {
        setRunningServices({});
      }
    } catch (error) {
      console.log('No se pudo conectar al Prodigy Manager');
      setRunningServices({});
    }
  };

  const fetchMyProgress = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/my-progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyProgress(data.progress || {});
        // Preseleccionar tipo para sesión extra: el primer tipo completado sin sesión extra activa
        const completedTypes = Object.entries(data.progress || {})
          .filter(([, info]) => info.completed && !info.extra_session && info.next_batch)
          .map(([key]) => key);
        if (completedTypes.length > 0) {
          setSelectedExtraType(prev => prev || completedTypes[0]);
        }
      }
    } catch (error) {
      console.log('No se pudo obtener progreso personal');
    }
  };

  const fetchMyBatches = async () => {
    try {
      setLoadingBatches(true);
      const resp = await prodigyGroupsAPI.getMyBatches();
      setMyBatches(resp.data || { groups: [] });
    } catch (error) {
      console.log('No se pudo obtener lista de batches asignados');
    } finally {
      setLoadingBatches(false);
    }
  };

  const openBatchInstance = (serviceId) => {
    const token = localStorage.getItem('access_token');
    const username = user?.username || user?.email || 'annotator';
    const url = `${PRODIGY_SESSION_URL}/?token=${encodeURIComponent(token)}&service=${encodeURIComponent(serviceId)}&session=${encodeURIComponent(username)}`;
    window.open(url, `prodigy_${serviceId}`, 'width=1400,height=900');
  };

  const [startingBatch, setStartingBatch] = useState({});
  const handleStartBatchInstance = async (serviceId) => {
    setStartingBatch(prev => ({ ...prev, [serviceId]: true }));
    try {
      const resp = await prodigyGroupsAPI.startBatchInstance(serviceId);
      // Refrescar lista y abrir Prodigy
      await fetchMyBatches();
      if (resp.data?.success) {
        // Esperar un instante para que Prodigy esté listo
        await new Promise(r => setTimeout(r, 1500));
        openBatchInstance(serviceId);
      }
    } catch (e) {
      alert(e.response?.data?.detail || `Error al iniciar ${serviceId}`);
    } finally {
      setStartingBatch(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const fetchExtraProcesses = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/extra-processes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExtraProcesses(data.extra_processes || []);
      }
    } catch (error) {
      console.log('No se pudo obtener sesiones extra');
    }
  };

  const fetchAllPorts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/admin/all-ports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllPorts(data.ports || []);
      }
    } catch (error) {
      console.log('No se pudo obtener puertos activos');
    }
  };

  const fetchBufferConfig = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/config/buffer`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBufferConfig(data.config || bufferConfig);
      }
    } catch (error) {
      console.log('No se pudo obtener configuración de buffer');
    }
  };

  const saveBufferConfig = async () => {
    setSavingConfig(true);
    setConfigError(null);
    setConfigSuccess(false);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/config/buffer`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bufferConfig),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error guardando configuración');
      setConfigSuccess(true);
      // Mensaje persiste más tiempo para indicar necesidad de reinicio
      setTimeout(() => setConfigSuccess(false), 8000);
    } catch (error) {
      setConfigError(error.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const resetBufferConfig = async () => {
    setSavingConfig(true);
    setConfigError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/config/buffer/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error restableciendo configuración');
      setBufferConfig(data.config);
      setConfigSuccess(true);
      setTimeout(() => setConfigSuccess(false), 8000);
    } catch (error) {
      setConfigError(error.message);
    } finally {
      setSavingConfig(false);
    }
  };

  // Reiniciar todos los servicios activos para aplicar nueva config
  const restartAllServices = async () => {
    setIsStarting(true);
    setStartError(null);
    const activeServices = Object.entries(runningServices)
      .filter(([_, info]) => info?.running)
      .map(([service, info]) => ({ service, batch: info.batch || selectedBatch }));
    
    try {
      const token = localStorage.getItem('access_token');
      // Detener todos
      for (const { service } of activeServices) {
        await fetch(`${PRODIGY_MANAGER_URL}/api/stop-prodigy/${service}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      // Esperar un momento
      await new Promise(r => setTimeout(r, 2000));
      // Reiniciar todos
      for (const { service, batch } of activeServices) {
        await fetch(`${PRODIGY_MANAGER_URL}/api/start-prodigy`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipe: 'batch', annotation_type: service, batch_number: batch })
        });
        await new Promise(r => setTimeout(r, 1000));
      }
      setConfigSuccess(true);
      setTimeout(() => setConfigSuccess(false), 3000);
    } catch (error) {
      setStartError(`Error reiniciando: ${error.message}`);
    } finally {
      setIsStarting(false);
      setTimeout(fetchRunningServices, 2000);
    }
  };

  const startExtraSession = async (annotationType) => {
    setStartingExtra(true);
    setExtraError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/start-extra`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ annotation_type: annotationType }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error iniciando sesión extra');
      const username = user?.username || user?.email || 'annotator';
      const url = `${PRODIGY_SESSION_URL}/?token=${encodeURIComponent(token)}&extra=${encodeURIComponent(data.dataset)}&session=${encodeURIComponent(username)}`;
      window.open(url, `prodigy_extra_${annotationType}`, 'width=1400,height=900');
      await fetchMyProgress();
      await fetchExtraProcesses();
    } catch (error) {
      setExtraError(error.message);
    } finally {
      setStartingExtra(false);
    }
  };

  const stopExtraSession = async (datasetName) => {
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${PRODIGY_MANAGER_URL}/api/stop-extra/${encodeURIComponent(datasetName)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      await fetchMyProgress();
      await fetchExtraProcesses();
    } catch (error) {
      console.error('Error deteniendo sesión extra:', error);
    }
  };

  const exportBackupCsv = async ({ onlyAccepted = false, noSpansJson = false } = {}) => {
    setExportingBackup(true);
    setBackupError(null);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setBackupError('No hay sesión activa. Iniciá sesión e intentá de nuevo.');
        return;
      }
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/admin/export-backup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ only_accepted: onlyAccepted, no_spans_json: noSpansJson }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `Error ${response.status}`);
      }
      // Disparar descarga del CSV
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `backup_annotations_${Date.now()}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setBackupError(error.message);
    } finally {
      setExportingBackup(false);
    }
  };

  const openExtraSession = (datasetName) => {
    const token = localStorage.getItem('access_token');
    const username = user?.username || user?.email || 'annotator';
    const url = `${PRODIGY_SESSION_URL}/?token=${encodeURIComponent(token)}&extra=${encodeURIComponent(datasetName)}&session=${encodeURIComponent(username)}`;
    window.open(url, `prodigy_extra_open`, 'width=1400,height=900');
  };

  const isServiceOnline = (service) => {
    // Check direct key (legacy) AND group-style keys (pos-g0, pos-g1, …)
    if (runningServices[service]?.running === true) return true;
    return Object.entries(runningServices).some(
      ([svcId, state]) => state?.running === true && svcId.startsWith(`${service}-g`)
    );
  };

  const getRunningInfo = (service) => {
    if (runningServices[service]) return runningServices[service];
    const entry = Object.entries(runningServices).find(
      ([svcId, state]) => state?.running === true && svcId.startsWith(`${service}-g`)
    );
    return entry ? entry[1] : null;
  };

  const startProdigyService = async (service, batch) => {
    setIsStarting(true);
    setStartError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${PRODIGY_MANAGER_URL}/api/start-prodigy`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe: 'batch', annotation_type: service, batch_number: batch })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error iniciando servicio');
      setTimeout(fetchRunningServices, 2000);
      return data;
    } catch (error) {
      setStartError(error.message);
      throw error;
    } finally {
      setIsStarting(false);
    }
  };

  const stopProdigyService = async (service) => {
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${PRODIGY_MANAGER_URL}/api/stop-prodigy/${service}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTimeout(fetchRunningServices, 1000);
    } catch (error) {
      console.error('Error deteniendo servicio:', error);
    }
  };

  const openProdigyService = async (service) => {
    const config = services[service];
    if (!config) return;
    const token = localStorage.getItem('access_token');
    if (!token) { alert('No hay sesión activa'); return; }
    
    // Solo admins pueden iniciar servicios automáticamente
    if (!isServiceOnline(service)) {
      if (!isAdmin) {
        alert('El servicio no está activo. Contacta a un administrador para iniciarlo.');
        return;
      }
      try {
        await startProdigyService(service, selectedBatch);
        await new Promise(resolve => setTimeout(resolve, 3000));
      } catch (error) {
        alert(`Error: ${error.message}`);
        return;
      }
    }
    
    const username = user?.username || user?.email || 'annotator';
    const proxyUrl = `${PRODIGY_SESSION_URL}/?token=${encodeURIComponent(token)}&service=${service}&session=${encodeURIComponent(username)}`;
    window.open(proxyUrl, `prodigy_${service}`, 'width=1400,height=900');
  };

  // Servicios visibles: admins ven todos; anotadores solo los que tienen asignados
  // myProgress keys are svc_ids like "pos-g0" — extract base annotation_type for matching
  const myProgressBaseTypes = new Set(
    Object.keys(myProgress).map(key => key.replace(/-g\d+$/, ''))
  );
  const visibleServiceEntries = isAdmin
    ? Object.entries(services)
    : Object.entries(services).filter(([key]) => myProgressBaseTypes.has(key));

  const onlineCount = visibleServiceEntries.filter(([key]) => isServiceOnline(key)).length;
  const totalServices = visibleServiceEntries.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 64px)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Sistema de Anotación Guaraní</h1>
          <p className="text-gray-500 text-sm">{totalBatches} lotes × {BATCH_SIZE} oraciones = {totalBatches * BATCH_SIZE} total</p>
        </div>

        {/* Status Bar */}
        <div className={`card mb-6 flex items-center justify-between ${onlineCount > 0 ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
          <div className="flex items-center gap-3">
            <span className={`inline-block w-3 h-3 rounded-full ${onlineCount > 0 ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            <span className="font-medium text-gray-700">{onlineCount}/{totalServices} servicios activos</span>
          </div>
          <button 
            onClick={fetchRunningServices}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            ↻ Actualizar
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Configuration (Solo para Admins) */}          <div className="space-y-6">
            {/* Config Card - Solo visible para admins */}
            {isAdmin ? (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  <span className="mr-2">⚙️</span>Panel de Administración
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Anotación</label>
                    <select
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      className="input"
                    >
                      {Object.entries(services).map(([key, config]) => (
                        <option key={key} value={key}>{config.emoji} {config.step}. {config.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Lote (1-{totalBatches})</label>
                    <input
                      type="number"
                      min="1"
                      max={totalBatches}
                      value={selectedBatch}
                      onChange={(e) => setSelectedBatch(Math.max(1, Math.min(totalBatches, parseInt(e.target.value) || 1)))}
                      className="input"
                    />
                  </div>

                  <button
                    onClick={() => startProdigyService(selectedService, selectedBatch)}
                    disabled={isStarting}
                    className={`w-full btn-primary ${isStarting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isStarting ? 'Iniciando...' : 'Iniciar Servicio'}
                  </button>
                  
                  {startError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {startError}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📝</span>
                  <div>
                    <h3 className="font-semibold text-blue-900">Modo Anotador</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Puedes abrir los servicios de anotación que estén activos. 
                      Contacta a un administrador si necesitas iniciar un servicio.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Selection - Solo visible para admins */}
            {isAdmin && (
              <div className="card">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Selección rápida de lote</h3>
                <div className="flex flex-wrap gap-2">
                  {[1, 5, 10, 20, 30, 40, 50, 60].map(batch => (
                    <button
                      key={batch}
                      onClick={() => setSelectedBatch(batch)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        selectedBatch === batch 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {batch}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Configuración de Buffer - Solo visible para admins */}
            {isAdmin && (
              <div className="card">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowBufferConfig(!showBufferConfig)}
                >
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span>💾</span> Configuración de Buffer
                  </h3>
                  <span className={`transform transition-transform ${showBufferConfig ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </div>
                
                {showBufferConfig && (
                  <div className="mt-4 space-y-4 border-t pt-4">
                    {/* Instant Submit Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Envío instantáneo</label>
                        <p className="text-xs text-gray-500">Guardar cada anotación inmediatamente</p>
                      </div>
                      <button
                        onClick={() => setBufferConfig(prev => ({ ...prev, instant_submit: !prev.instant_submit }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          bufferConfig.instant_submit ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          bufferConfig.instant_submit ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* Batch Size Slider - Solo visible si instant_submit está desactivado */}
                    {!bufferConfig.instant_submit && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Tamaño del buffer</label>
                          <span className="text-sm font-bold text-blue-600">{bufferConfig.batch_size}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={bufferConfig.batch_size}
                          onChange={(e) => setBufferConfig(prev => ({ ...prev, batch_size: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>1</span>
                          <span>5</span>
                          <span>10</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Número de anotaciones a acumular antes de enviar al servidor
                        </p>
                      </div>
                    )}

                    {/* Estado visual del modo */}
                    <div className={`p-3 rounded-lg text-sm ${
                      bufferConfig.instant_submit 
                        ? 'bg-green-50 border border-green-200 text-green-700' 
                        : 'bg-blue-50 border border-blue-200 text-blue-700'
                    }`}>
                      {bufferConfig.instant_submit ? (
                        <span>⚡ <strong>Modo instantáneo:</strong> Cada anotación se guarda inmediatamente</span>
                      ) : (
                        <span>📦 <strong>Modo buffer:</strong> Se acumulan {bufferConfig.batch_size} anotaciones antes de enviar</span>
                      )}
                    </div>

                    {/* Botones de acción */}
                    <div className="flex gap-2">
                      <button
                        onClick={saveBufferConfig}
                        disabled={savingConfig}
                        className={`flex-1 btn-primary text-sm ${savingConfig ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {savingConfig ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        onClick={resetBufferConfig}
                        disabled={savingConfig}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Restablecer
                      </button>
                    </div>

                    {/* Mensajes de éxito/error */}
                    {configSuccess && (
                      <div className="space-y-2">
                        <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm text-center">
                          ✓ Configuración guardada
                        </div>
                        {Object.values(runningServices).some(s => s?.running) && (
                          <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                            <p className="font-medium">⚠️ Servicios activos detectados</p>
                            <p className="text-xs mt-1">Los cambios se aplicarán al reiniciar los servicios.</p>
                            <button
                              onClick={restartAllServices}
                              disabled={isStarting}
                              className="mt-2 w-full py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
                            >
                              {isStarting ? 'Reiniciando...' : '🔄 Reiniciar servicios ahora'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {configError && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                        {configError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Puertos Activos - Solo para admins */}
            {isAdmin && (
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span>🖥️</span> Puertos Activos
                  </h3>
                  <button
                    onClick={fetchAllPorts}
                    className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    ↺ Actualizar
                  </button>
                </div>

                {/* Backup CSV */}
                <div className="mb-3 p-2 rounded-lg bg-gray-50 border border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">📥 Backup de anotaciones</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => exportBackupCsv()}
                      disabled={exportingBackup}
                      className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                      title="Descarga CSV completo (incluye tokens y spans JSON)"
                    >
                      {exportingBackup ? 'Generando...' : '⬇ Completo'}
                    </button>
                    <button
                      onClick={() => exportBackupCsv({ noSpansJson: true })}
                      disabled={exportingBackup}
                      className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      title="Descarga CSV liviano sin columnas tokens_json/spans_json"
                    >
                      {exportingBackup ? '...' : '⬇ Liviano'}
                    </button>
                    <button
                      onClick={() => exportBackupCsv({ onlyAccepted: true, noSpansJson: false })}
                      disabled={exportingBackup}
                      className="text-xs px-2 py-1 rounded bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
                      title="Solo anotaciones aceptadas"
                    >
                      {exportingBackup ? '...' : '⬇ Solo aceptadas'}
                    </button>
                  </div>
                  {backupError && (
                    <p className="text-xs text-red-500 mt-1">{backupError}</p>
                  )}
                </div>
                {allPorts.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No hay procesos corriendo.</p>
                ) : (
                  <div className="space-y-2">
                    {allPorts.map((p) => {
                      const kindColor = {
                        shared: 'bg-blue-100 text-blue-700',
                        reannotation: 'bg-amber-100 text-amber-700',
                        extra: 'bg-emerald-100 text-emerald-700',
                      }[p.kind] || 'bg-gray-100 text-gray-700';
                      const kindLabel = { shared: 'Compartido', reannotation: 'Re-anot.', extra: 'Extra' }[p.kind] || p.kind;
                      return (
                        <div key={`${p.port}-${p.dataset || p.kind}`} className="flex items-start justify-between gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${kindColor}`}>{kindLabel}</span>
                              <span className="text-xs font-semibold text-gray-800">:{p.port}</span>
                              <span className="text-xs text-gray-600 truncate">{p.name}</span>
                              {p.batch && <span className="text-xs text-gray-400">Lote {p.batch}</span>}
                            </div>
                            {p.username && (
                              <p className="text-xs text-gray-400 mt-0.5">usuario: {p.username}</p>
                            )}
                            {p.dataset && (
                              <p className="text-xs text-gray-300 truncate">{p.dataset}</p>
                            )}
                          </div>
                          <a
                            href={`http://localhost:8085/?service=${p.annotation_type}&token=${localStorage.getItem('access_token')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-shrink-0 text-xs text-blue-500 hover:text-blue-700 mt-0.5"
                            title="Abrir en nueva pestaña"
                          >
                            ↗
                          </a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* User Info */}
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                  {(user?.username || user?.email || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{user?.username || user?.email}</p>
                  <p className="text-xs text-gray-500">{isAdmin ? 'Administrador' : 'Anotador'}</p>
                </div>
              </div>
            </div>

            {/* Mi progreso por tipo */}
            {Object.keys(myProgress).length > 0 && (
              <div className="card">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Mi progreso</h3>
                <div className="space-y-2">
                  {Object.entries(myProgress).map(([key, info]) => {
                    const pct = Math.min(100, Math.round((info.count / info.quota) * 100));
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                          <span>{services[key]?.name || key} — Lote {info.current_batch}</span>
                          <span className={info.completed ? 'text-emerald-600 font-semibold' : ''}>
                            {info.count}/{info.quota}{info.completed ? ' ✓' : ''}
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${info.completed ? 'bg-emerald-500' : 'bg-blue-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Indicador de Buffer para todos los usuarios */}
            {!isAdmin && (
              <div className="card bg-gray-50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{bufferConfig.instant_submit ? '⚡' : '📦'}</span>
                  <span>
                    {bufferConfig.instant_submit 
                      ? 'Guardado instantáneo' 
                      : `Buffer: ${bufferConfig.batch_size} anotaciones`
                    }
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Services */}
          <div className="lg:col-span-2 space-y-4">
            {/* Pipeline Flow */}
            <div className="card">
              <div className="flex items-center justify-center gap-4">
                {visibleServiceEntries.map(([key, config], index, arr) => (
                  <div key={key} className="flex items-center">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isServiceOnline(key) 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {config.emoji} {config.step}. {config.name} {isServiceOnline(key) && '✓'}
                    </span>
                    {index < arr.length - 1 && <span className="mx-2 text-gray-400">→</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* NC-68: Mis Batches Asignados (anotadores, vista multi-instancia) */}
            {!isAdmin && (myBatches.groups?.length > 0 || loadingBatches) && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span>📋</span> Mis batches asignados
                  </h3>
                  <button
                    onClick={fetchMyBatches}
                    className="text-xs text-blue-600 hover:underline"
                    disabled={loadingBatches}
                  >
                    {loadingBatches ? '⏳' : '🔄'} Actualizar
                  </button>
                </div>
                {myBatches.groups?.length === 0 && !loadingBatches ? (
                  <p className="text-sm text-gray-500">No tenés grupos asignados todavía.</p>
                ) : (
                  <div className="space-y-4">
                    {(myBatches.groups || []).map((g) => (
                      <div key={g.group_id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-gray-800">
                              {g.group_name}
                              <span className="ml-2 text-xs text-gray-500">({g.annotation_type})</span>
                            </h4>
                          </div>
                          <span className="text-xs text-gray-500">
                            {(g.batches || []).filter(b => b.running).length} / {(g.batches || []).length} activas
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {(g.batches || []).map((b) => {
                            const pct = b.quota > 0 ? Math.min(100, Math.round((b.my_count / b.quota) * 100)) : 0;
                            return (
                              <div
                                key={b.service_id}
                                className={`p-2 rounded border text-xs bg-white ${
                                  b.completed_by_me
                                    ? 'border-emerald-300'
                                    : b.running
                                    ? 'border-blue-300'
                                    : 'border-gray-200 opacity-70'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-semibold text-gray-700">Batch {b.batch}</span>
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                      b.running ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                    }`}
                                  >
                                    {b.running ? 'ONLINE' : 'OFF'}
                                  </span>
                                </div>
                                <div className="text-[11px] text-gray-600 mb-1">
                                  {b.my_count} / {b.quota}
                                  {b.completed_by_me && <span className="ml-1 text-emerald-600">✓</span>}
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1 mb-2">
                                  <div
                                    className={`h-1 rounded-full ${
                                      b.completed_by_me ? 'bg-emerald-500' : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <button
                                  onClick={() => !b.completed_by_me && (b.running ? openBatchInstance(b.service_id) : handleStartBatchInstance(b.service_id))}
                                  disabled={startingBatch[b.service_id] || b.completed_by_me}
                                  className={`w-full text-[11px] py-1 rounded text-white disabled:cursor-not-allowed ${
                                    b.completed_by_me
                                      ? 'bg-emerald-400 opacity-60'
                                      : b.running
                                      ? 'bg-blue-600 hover:bg-blue-700'
                                      : 'bg-emerald-600 hover:bg-emerald-700'
                                  }`}
                                  title={b.completed_by_me ? 'Ya completaste la cuota de este batch' : b.running ? `Abrir Prodigy (puerto ${b.port})` : 'Iniciar instancia y abrir Prodigy'}
                                >
                                  {startingBatch[b.service_id]
                                    ? '⏳ Iniciando...'
                                    : b.completed_by_me
                                    ? '✓ Completado'
                                    : b.running
                                    ? 'Abrir Prodigy'
                                    : '▶ Iniciar'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Service Cards */}
            {!isAdmin && !loading && !loadingBatches && hasGroupWithoutBatches && (
              <div className="card bg-amber-50 border-amber-200">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h3 className="font-semibold text-amber-900">Tu grupo no tiene lotes asignados</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Estás en un grupo de anotación pero todavía no tiene un rango de lotes válido. Avisá a un administrador.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {!isAdmin && !loading && visibleServiceEntries.length === 0 && (myBatches.groups?.length || 0) === 0 && !hasGroupWithoutBatches && (
              <div className="card bg-amber-50 border-amber-200">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h3 className="font-semibold text-amber-900">Sin servicios asignados</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      No tenés ningún tipo de anotación asignado. Contactá a un administrador para que te agregue a un grupo.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {(isAdmin || ((myBatches.groups?.length || 0) === 0 && !hasGroupWithoutBatches)) && visibleServiceEntries.map(([key, config]) => {
              const isOnline = isServiceOnline(key);
              const runningInfo = getRunningInfo(key);
              
              return (
                <div key={key} className={`card ${isOnline ? 'border-l-4 border-green-500' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{config.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          isOnline ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{config.description}</p>
                      {runningInfo && isOnline && (
                        <p className="text-xs text-gray-500 mt-1">Lote {runningInfo.batch || '?'} • Puerto {runningInfo.port || config.port}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isOnline ? (
                        <>
                          {/* Botón Detener - Solo para admins */}
                          {isAdmin && (
                            <button
                              onClick={() => stopProdigyService(key)}
                              className="btn-danger text-sm"
                            >
                              Detener
                            </button>
                          )}
                          <button
                            onClick={() => openProdigyService(key)}
                            className="btn-success"
                          >
                            Abrir
                          </button>
                        </>
                      ) : (
                        /* Servicio offline */
                        isAdmin ? (
                          <button
                            onClick={() => openProdigyService(key)}
                            className="btn-primary"
                          >
                            Iniciar y Abrir
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            No disponible
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Keyboard Shortcuts */}
            <div className="card bg-gray-800 text-white">
              <h3 className="font-medium mb-3">Atajos de teclado en Prodigy</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><kbd className="px-2 py-1 bg-gray-700 rounded mr-2">A</kbd>Aceptar</div>
                <div><kbd className="px-2 py-1 bg-gray-700 rounded mr-2">X</kbd>Rechazar</div>
                <div><kbd className="px-2 py-1 bg-gray-700 rounded mr-2">Space</kbd>Ignorar</div>
                <div><kbd className="px-2 py-1 bg-gray-700 rounded mr-2">⌫</kbd>Deshacer</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProdigyPage;
